import { eventLocationLabel, findEventEntry } from "@repo/vihangam-yog-shared";
import { ExternalLink, ScanLine } from "lucide-react";
import { useMemo, useState } from "react";

import { useAuth } from "../auth/AuthContext";
import { CHAPTER } from "../chapter";
import { StatPlate } from "../components/DataKit";
import type { NavId } from "../components/Shell";
import { eventsTodayFromData, groupEventsForPicker } from "../lib/eventTiming";
import { trpc } from "../trpc";

import {
  plainTimeLabel,
  signedUpLabel,
} from "./event-registrations/guestFormat";
import { formatMoney, type EventGroup } from "./event-registrations/shared";

/**
 * Home: today's events at the door first (the one tap a volunteer needs
 * on the day), then the chapter's numbers and the latest bookings and
 * donations. Queries refetch when the tab regains focus; each part
 * renders as its data lands.
 */

/** Shown in a number plate while its figure is still loading. */
const LOADING_VALUE = "…";

/** "12 Sep": the day a booking or donation came in. */
function dayMonth(iso: string): string {
  return new Date(iso).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
  });
}

/** "Sydney" for an event in a city; null for online and weekly events. */
function cityOf(group: EventGroup): string | null {
  return group.eventLocation ? group.eventLocationLabel : null;
}

/** "Sydney" for a catalogue event; null for an unknown slug or no city. */
function locationFor(slug: string | null): string | null {
  if (!slug) {
    return null;
  }
  const entry = findEventEntry(slug);
  return entry?.location ? eventLocationLabel(entry.location) : null;
}

/** The parts of a meta line that exist, joined with a middle dot. */
function metaLine(parts: readonly (string | null)[]): string {
  return parts.filter((part) => part !== null && part !== "").join(" · ");
}

function peopleWord(count: number): string {
  return count === 1 ? "person" : "people";
}

export function Overview({ onNavigate }: { onNavigate: (id: NavId) => void }) {
  const { user } = useAuth();
  const firstName = user?.fullName?.trim().split(/\s+/)[0] ?? "";

  const eventRegs = trpc.adminListEventRegistrations.useQuery(undefined, {
    retry: false,
    refetchOnWindowFocus: true,
  });
  const donations = trpc.adminListDonations.useQuery(undefined, {
    retry: false,
    refetchOnWindowFocus: true,
  });
  // The six newest show by default; "Show all" lists every donation.
  const [showAllDonations, setShowAllDonations] = useState(false);
  const allDonations = donations.data?.donations ?? [];
  const recentDonations = showAllDonations
    ? allDonations
    : allDonations.slice(0, 6);

  // Today's events come from the bookings data, not the catalogue: the
  // data holds exactly what this build serves (Christchurch in the
  // Australian build, never Australian events in Singapore's), and each
  // event's date is judged in its own time zone.
  const groups = eventRegs.data?.events ?? [];
  const now = new Date();
  const today = eventsTodayFromData(groups, now);
  const nextUp =
    today.length === 0
      ? (groupEventsForPicker(groups, now).comingUp[0] ?? null)
      : null;

  // Newest bookings across every event, newest first — createdAt is a
  // fixed-width ISO timestamp, so a plain string sort is a correct
  // chronological sort with no Date parsing needed.
  const latestBookings = useMemo(
    () =>
      (eventRegs.data?.events ?? [])
        .flatMap((group) =>
          group.registrations.map((booking) => ({
            booking,
            eventTitle: group.eventTitle,
            city: cityOf(group),
          })),
        )
        .sort((a, b) => b.booking.createdAt.localeCompare(a.booking.createdAt))
        .slice(0, 5),
    [eventRegs.data],
  );

  function openReception(slug: string) {
    window.history.pushState({}, "", `/reception/${encodeURIComponent(slug)}`);
    onNavigate("reception");
  }

  return (
    <section className="mx-auto max-w-4xl px-5 py-8 md:px-12 md:py-12">
      <p className="text-[16px] text-[color:var(--color-ink-soft)]">
        {firstName ? `Hi ${firstName}` : "Hi"}
      </p>
      <h1 className="page-title mt-1">{CHAPTER.name}</h1>

      {/* ── Today at the door ── */}
      {today.length > 0 ? (
        <div className="mt-8">
          <h2 className="flex items-center gap-2 text-[14px] font-semibold text-[color:var(--color-ink-soft)]">
            <ScanLine
              size={16}
              aria-hidden
              className="text-[color:var(--color-amber)]"
            />
            Today at the door
          </h2>
          <ul className="mt-3 grid gap-4 md:grid-cols-2">
            {today.map((group) => {
              const timeLabel = findEventEntry(group.eventSlug)?.timeLabel;
              return (
                <li
                  key={group.eventSlug}
                  className="surface flex flex-col border-[color:var(--color-amber)]/60 p-5"
                >
                  <h3 className="text-[20px] leading-snug font-bold text-[color:var(--color-ink)]">
                    {group.eventTitle}
                  </h3>
                  <p className="mt-1 text-[15px] text-[color:var(--color-ink-soft)]">
                    {metaLine([
                      group.eventDateLabel,
                      timeLabel ? plainTimeLabel(timeLabel) : null,
                      cityOf(group),
                    ])}
                  </p>
                  <p className="mt-3 text-[16px] text-[color:var(--color-ink)]">
                    <span className="text-[28px] leading-none font-bold tabular-nums">
                      {group.checkedInPeople}
                    </span>{" "}
                    of <span className="tabular-nums">{group.totalPeople}</span>{" "}
                    {peopleWord(group.totalPeople)} here
                  </p>
                  <button
                    type="button"
                    onClick={() => openReception(group.eventSlug)}
                    className="mt-4 inline-flex min-h-11 cursor-pointer items-center justify-center gap-2 self-start rounded-full bg-[color:var(--color-amber)] px-5 text-[15px] font-semibold text-[color:var(--color-paper)] transition-opacity hover:opacity-90"
                  >
                    <ScanLine size={16} aria-hidden />
                    Open Reception
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : eventRegs.data ? (
        <p className="mt-6 text-[16px] text-[color:var(--color-ink-soft)]">
          {nextUp
            ? `No events today. Next up: ${nextUp.eventTitle}, ${nextUp.eventDateLabel}.`
            : "No events today."}
        </p>
      ) : null}

      {/* ── The numbers ── */}
      <div className="mt-8 grid grid-cols-2 gap-3 sm:flex sm:flex-wrap sm:gap-4">
        <StatPlate
          value={
            eventRegs.data ? eventRegs.data.totalRegistrations : LOADING_VALUE
          }
          label="Bookings"
        />
        <StatPlate
          value={eventRegs.data ? eventRegs.data.totalPeople : LOADING_VALUE}
          label="People coming"
          tone="amber"
        />
        <StatPlate
          value={
            donations.data
              ? formatMoney(donations.data.totalCents)
              : LOADING_VALUE
          }
          label="Donations"
          tone="amber"
        />
      </div>

      {/* ── Latest bookings, across every event ── */}
      {latestBookings.length > 0 ? (
        <div className="surface mt-8 p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-[18px] font-semibold text-[color:var(--color-ink)]">
              Latest bookings
            </h2>
            <button
              type="button"
              onClick={() => onNavigate("events")}
              className="inline-flex min-h-11 cursor-pointer items-center rounded-full border border-[color:var(--color-line-strong)] px-4 text-[14px] font-semibold text-[color:var(--color-ink)] transition-colors hover:border-[color:var(--color-ink)]"
            >
              See all events
            </button>
          </div>
          <ul className="mt-2 divide-y divide-[color:var(--color-line)]">
            {latestBookings.map(({ booking, eventTitle, city }) => (
              <li
                key={booking.id}
                className="flex items-start justify-between gap-4 py-3"
              >
                <div className="min-w-0">
                  <p className="text-[16px] break-words text-[color:var(--color-ink)]">
                    {booking.name}
                  </p>
                  <p className="text-[14px] break-words text-[color:var(--color-ink-soft)]">
                    {metaLine([
                      eventTitle,
                      city,
                      signedUpLabel(booking.createdAt),
                    ])}
                  </p>
                </div>
                <span className="shrink-0 text-[14px] text-[color:var(--color-ink-soft)] tabular-nums">
                  {booking.totalPeople} {peopleWord(booking.totalPeople)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* ── Latest donations: /donate, and gifts made with a booking ── */}
      {donations.data && donations.data.count > 0 ? (
        <div className="surface mt-8 p-5">
          <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
            <h2 className="text-[18px] font-semibold text-[color:var(--color-ink)]">
              Latest donations
            </h2>
            <p className="text-[14px] text-[color:var(--color-ink-soft)] tabular-nums">
              {donations.data.count}{" "}
              {donations.data.count === 1 ? "donation" : "donations"} ·{" "}
              {formatMoney(donations.data.totalCents)} in total
            </p>
          </div>
          <ul className="mt-2 divide-y divide-[color:var(--color-line)]">
            {recentDonations.map((d) => (
              <li
                key={d.id}
                className="flex items-start justify-between gap-4 py-3"
              >
                <div className="min-w-0">
                  <p className="text-[16px] break-words text-[color:var(--color-ink)]">
                    {d.name || d.email || "Anonymous"}
                  </p>
                  <p className="text-[14px] break-words text-[color:var(--color-ink-soft)]">
                    {metaLine([
                      d.eventTitle ?? "General donation",
                      locationFor(d.eventSlug),
                      dayMonth(d.createdAt),
                    ])}
                  </p>
                </div>
                <div className="flex shrink-0 flex-col items-end">
                  <span className="text-[16px] font-semibold text-[color:var(--color-ink)] tabular-nums">
                    {formatMoney(d.amountCents, d.currency)}
                  </span>
                  {d.receiptUrl ? (
                    <a
                      href={d.receiptUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex min-h-11 items-center gap-1 text-[14px] text-[color:var(--color-ink-soft)] underline underline-offset-4 hover:text-[color:var(--color-ink)]"
                    >
                      Receipt
                      <ExternalLink size={14} aria-hidden />
                      <span className="sr-only"> (opens in a new tab)</span>
                    </a>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
          {allDonations.length > 6 ? (
            <button
              type="button"
              onClick={() => setShowAllDonations((open) => !open)}
              aria-expanded={showAllDonations}
              className="mt-2 inline-flex min-h-11 items-center rounded-full border border-[color:var(--color-line-strong)] px-4 text-[15px] font-semibold text-[color:var(--color-ink)] transition-colors hover:border-[color:var(--color-ink)]"
            >
              {showAllDonations
                ? "Show the latest 6"
                : `Show all ${allDonations.length} donations`}
            </button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
